import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import { cx } from '@orn/shared-web/utils/cx';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../paths';
import { useAssistantSidebar } from '../../providers/AssistantSidebarProvider';
import { BRAND_COLOR } from '../logos/mantleLogo';
import MantleMarkPrimary from '../logos/MantleMarkPrimary';

const MARK_SIZE_FULL_PX = 16;
const MARK_SIZE_COMPACT_PX = 18;

interface IMantleAssistantNavButtonProps {
  /**
   * `full` (default) renders the wide footer pill with the Mantle mark
   * plus "Mantle Assistant" label, used inside the desktop sidebar and
   * the mobile drawer. `compact` renders the 36px square icon-only
   * variant used inside the 64px tablet icon rail.
   */
  variant?: 'full' | 'compact';
}

/**
 * Footer entry-point for the Mantle Assistant.
 * Visually distinct from the regular nav list — a mist-colored pill
 * (`bg-utility-mist-400`) with the Mantle mark and label per Figma node
 * `4319:92664` (full) and `4531:24287` "Mantle AI Button" (compact). The
 * caller (`FullSidebarContent` / `IconRailSidebar` / `MobileNavDrawer`)
 * is responsible for gating visibility behind `feat-assistant`; this
 * component renders unconditionally when mounted.
 *
 * **Behavior depends on viewport.** At `xl+` the assistant docks as a side
 * panel in the shell, so the full pill is a `<button>` that toggles the
 * panel's visibility (with `aria-pressed` / `aria-expanded` reflecting the
 * open state). Below `xl` — and for the icon-rail compact variant, which only
 * renders at `md` — there is no panel, so it stays a `RouterLink` to the
 * full-page `/assistant` route.
 */
const MantleAssistantNavButton = ({ variant = 'full' }: IMantleAssistantNavButtonProps) => {
  const { isOpen, toggle } = useAssistantSidebar();
  const isXl = useBreakpoint('xl');

  if (variant === 'compact') {
    return (
      <RouterLink
        to={PATHS.ASSISTANT}
        aria-label="Mantle Assistant"
        className={cx(
          'bg-utility-mist-400 border-utility-mist-500 outline-focus-ring',
          'flex size-9 shrink-0 items-center justify-center rounded-lg border',
          'transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2'
        )}
      >
        <MantleMarkPrimary size={MARK_SIZE_COMPACT_PX} color={BRAND_COLOR} aria-hidden="true" />
      </RouterLink>
    );
  }

  const pillClassName = cx(
    'bg-utility-mist-400 border-utility-mist-500 outline-focus-ring',
    'flex w-full items-center justify-center gap-2 rounded-md border px-4 py-3',
    'cursor-pointer transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2'
  );

  const pillContent = (
    <>
      <MantleMarkPrimary size={MARK_SIZE_FULL_PX} color={BRAND_COLOR} aria-hidden="true" />
      <span className="text-md font-semibold" style={{ color: BRAND_COLOR }}>
        Mantle Assistant
      </span>
    </>
  );

  // xl+: toggle the docked side panel. Below xl: navigate to the route.
  if (isXl) {
    return (
      <button
        type="button"
        onClick={toggle}
        // Disclosure pattern: `aria-expanded` reflects the docked panel's state.
        // Not paired with `aria-pressed` — ARIA forbids both on one control.
        aria-expanded={isOpen}
        aria-label={isOpen ? 'Hide Mantle Assistant' : 'Show Mantle Assistant'}
        className={pillClassName}
      >
        {pillContent}
      </button>
    );
  }

  return (
    <RouterLink to={PATHS.ASSISTANT} className={pillClassName}>
      {pillContent}
    </RouterLink>
  );
};

export default MantleAssistantNavButton;
